import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Knopf } from "../design/Kachel";
import { Textfeld } from "../design/Formular";
import { Abweisung, Meldung } from "../design/Hinweis";
import { Eingang } from "../design/Ansicht";
import { Symbol } from "../design/Symbol";
import { zugang } from "../plattform/api";
import { geraetebezeichnungVorschlag, passkeyUnterstuetzt } from "../plattform/geraet";
import { mitCustomTokenAnmelden } from "../plattform/sitzung";
import { usePasskeyzeremonie, type Zeremonieergebnis } from "./passkey";
import { EINLADUNG_OHNE_PASSKEY, OHNE_NETZ, passkeymeldung, type Ort } from "./zugangsmeldungen";

const VORBEREITEN_NACH_MS = 400;

/**
 * Einladung (oberflaeche.md §7, identitaeten.md §7, ADR-0015). Der Token steht im Fragment und erreicht den Server beim
 * Laden nie; das Öffnen allein meldet niemanden an. Erst eine bewusste Handlung nimmt an, meldet an oder nutzt den Link.
 */

interface Begruessung { Revier: string; Name: string; Anrede: string; Ansprachform?: "Du" | "Sie" }
/** Hinweis zur Abweisung eines Links (identitaeten.md §7.2): Ansprechpartner, Ansprachform des Reviers, Grund der Deaktivierung. */
interface Hinweis { Jagdherr: string | null; Ansprachform: "Du" | "Sie"; Grund?: string }

export function begruessungstext(b: Begruessung): string {
  return b.Anrede === "ohne Anrede" ? `Willkommen im Revier ${b.Revier}, ${b.Name}` : `Willkommen im Revier ${b.Revier}, ${b.Anrede} ${b.Name}`;
}

/** Der Link wurde schon mit einem Passkey angenommen (Grund `verknüpft`): Anmelden statt eines Ansprechpartners (V-23). */
export function schonAngenommen(h: Hinweis | null): boolean {
  return h?.Grund === "verknüpft";
}

/**
 * Meldung zu einem Link, der nicht mehr gilt, in der Ansprachform des Reviers (oberflaeche.md §9): schon angenommen → zur
 * Anmeldung (V-23); ersetzt, abgelaufen oder von Hand deaktiviert → „Link nicht mehr gültig“ mit dem Ansprechpartner.
 */
export function ungueltigText(h: Hinweis | null): string {
  if (schonAngenommen(h)) {
    return h!.Ansprachform === "Sie"
      ? "Diese Einladung haben Sie schon angenommen. Öffnen Sie die App und tippen Sie auf „Anmelden“."
      : "Diese Einladung hast du schon angenommen. Öffne die App und tippe auf „Anmelden“.";
  }
  if (!h?.Jagdherr) return "Dieser Link ist nicht mehr gültig.";
  return h.Ansprachform === "Sie"
    ? `Dieser Link ist nicht mehr gültig. Bitte wenden Sie sich an ${h.Jagdherr}.`
    : `Dieser Link ist nicht mehr gültig. Bitte wende dich an ${h.Jagdherr}.`;
}

export function Einladung() {
  const navigate = useNavigate();
  const [token] = useState(() => decodeURIComponent(window.location.hash.slice(1)));
  const [begruessung, setBegruessung] = useState<Begruessung | null>(null);
  const [ungueltig, setUngueltig] = useState<{ grund: string; hinweis: Hinweis | null } | null>(null);
  const [geraet, setGeraet] = useState(geraetebezeichnungVorschlag);
  const [grund, setGrund] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  // Ein Gerät, das beim Versuch meldet, dass es keine Passkeys kann, gilt als Gerät ohne Passkey-Unterstützung (§7, V-20).
  const [ohnePasskey, setOhnePasskey] = useState(() => !passkeyUnterstuetzt());
  const passkeys = !ohnePasskey;
  const erstellen = usePasskeyzeremonie("registrierung");
  const verknuepfen = usePasskeyzeremonie("anmeldung");
  const annehmenAngaben = { zweck: "einladung", Einladungstoken: token, "Gerätebezeichnung": geraet };
  const anmeldenAngaben = { zweck: "einladung", Einladungstoken: token };
  const begonnen = useRef(false);

  useEffect(() => {
    if (!token) {
      setUngueltig({ grund: "einladung-deaktiviert", hinweis: null });
      return;
    }
    void zugang("link/pruefen", { Einladungstoken: token }).then((a) => {
      if (a.ok) setBegruessung(a.felder["Begrüßung"] as Begruessung);
      else setUngueltig({ grund: a.grund ?? "einladung-deaktiviert", hinweis: (a.felder["Hinweis"] as Hinweis | undefined) ?? null });
    });
  }, [token]);

  // Optionen beider Zeremonien bereithalten, sobald die Einladung gilt: WebAuthn startet dann in der Geste (iOS ≤ 17.3).
  // Erst nach einer Pause im Tippen der Gerätebezeichnung, damit nicht jeder Buchstabe eine Zeremonie anlegt, und nie
  // nach dem ersten Fingertipp: Ein Abschluss verbraucht die Einladung, eine spätere Vorbereitung würde abgewiesen.
  useEffect(() => {
    if (!begruessung) return undefined;
    const t = setTimeout(() => {
      if (begonnen.current) return;
      erstellen.vorbereiten({ zweck: "einladung", Einladungstoken: token, "Gerätebezeichnung": geraet });
      verknuepfen.vorbereiten({ zweck: "einladung", Einladungstoken: token });
    }, VORBEREITEN_NACH_MS);
    return () => clearTimeout(t);
  }, [begruessung, token, geraet, erstellen, verknuepfen]);

  function beginnen() {
    begonnen.current = true;
    setGrund(null);
    setMeldung(null);
    setLaeuft(true);
  }

  function nachZeremonie(ort: Ort, r: Zeremonieergebnis) {
    setLaeuft(false);
    if (!r.fehler) {
      navigate("/", { replace: true, state: { nachEinladung: true } });
      return;
    }
    if (r.fehler === "abgelehnt") setGrund(r.antwort.grund ?? "unzulaessige-angaben");
    else setMeldung(passkeymeldung(ort, r.fehler, begruessung?.Ansprachform ?? null));
    if (r.fehler === "nicht-unterstuetzt") setOhnePasskey(true);
  }

  // Beide Zeremonien starten im Handler des Fingertipps ohne vorheriges `await` (Passkeyzeremonie).
  function annehmen() {
    beginnen();
    void erstellen.ausfuehren(annehmenAngaben, () => verknuepfen.vorgeholt()).then((r) => nachZeremonie("einladung-annehmen", r));
  }
  function anmelden() {
    beginnen();
    void verknuepfen.ausfuehren(anmeldenAngaben, () => erstellen.vorgeholt()).then((r) => nachZeremonie("einladung-anmelden", r));
  }
  async function linkZugang() {
    beginnen();
    const a = await zugang("link/anmelden", { Einladungstoken: token, "Gerätebezeichnung": geraet, "bestätigt": true });
    if (a.ok) await mitCustomTokenAnmelden(String(a.felder["customToken"]));
    setLaeuft(false);
    if (a.ok) navigate("/", { replace: true, state: { nachEinladung: true } });
    else if (a.netzfehler) setMeldung(OHNE_NETZ);
    else setGrund(a.grund ?? "unzulaessige-angaben");
  }

  // Referenz `#s-einladung`: Begrüßung als Überschrift (Ebene 1), kurze Absätze im Scrollbereich, Gerätebezeichnung,
  // Handlungen fest unten („Annehmen“ groß, „Anmelden“ und „Wiederherstellung“ daneben)
  if (!begruessung) {
    return (
      <Eingang name="Einladung" titel="Einladung" unter="Einladung"
        unten={ungueltig && schonAngenommen(ungueltig.hinweis)
          ? <Knopf symbol="passkey" art="planung" onPress={() => navigate("/", { replace: true })}>Anmelden</Knopf> : undefined}>
        {ungueltig ? <Meldung symbol={schonAngenommen(ungueltig.hinweis) ? "haken" : "abgewiesen"}>{ungueltigText(ungueltig.hinweis)}</Meldung>
          : <p className="leer platte" aria-busy="true"><Symbol name="einladung" /><span>Einladung wird geprüft</span></p>}
      </Eingang>
    );
  }
  return (
    <Eingang name="Einladung" titel={begruessungstext(begruessung)} unter={`Einladung · Revier ${begruessung.Revier}`} drei={passkeys} unten={passkeys ? (
      <>
        <Knopf symbol="passkey" art="planung" onPress={annehmen} isDisabled={laeuft}>Annehmen</Knopf>
        <Knopf symbol="passkey" art="planung-zweit" onPress={anmelden} isDisabled={laeuft}>Anmelden</Knopf>
        <Knopf art="planung-zweit" onPress={() => navigate("/wiederherstellung")}>Wiederherstellung</Knopf>
      </>
    ) : (
      <div className="knopfreihe">
        <Knopf symbol="link" art="planung" onPress={linkZugang} isDisabled={laeuft}>Link-Zugang</Knopf>
        <Knopf art="planung-zweit" onPress={() => navigate("/wiederherstellung")}>Wiederherstellung</Knopf>
      </div>
    )}>
      {passkeys ? (
        <>
          <p className="erkl">Mit einem Passkey meldet sich dieses Gerät künftig an.</p>
          <p className="erkl">Fingerabdruck, Gesicht oder Geräte-PIN genügen, ohne Passwort.</p>
        </>
      ) : meldung === EINLADUNG_OHNE_PASSKEY ? null : <p className="erkl">{EINLADUNG_OHNE_PASSKEY}</p>}
      <Textfeld label="Gerätebezeichnung" wert={geraet} onChange={setGeraet} />
      <Abweisung grund={grund} />
      {meldung ? <Meldung>{meldung}</Meldung> : null}
    </Eingang>
  );
}
